import { Button, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ApiRequest } from "GlobalFunctions";
import { useState } from "react";
import { useSnackbar, withSnackbar } from 'notistack';


export default function ElavonForm(props: any) {

    const { 
        updatePlan, 
        amount, 
        style,
        setOpenDialog
} = props;

    const [formValues, setFormValues] = useState({
        name: "",
        card_number: "",
        card_expiration: "",
        card_cvv: "",
    });
    const [errors, setErrors] = useState({
        card_expiration: false,
    });
    const [loading, setLoading] = useState(false);

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    // Detectar el tipo de tarjeta basado en IIN (Issuer Identification Number)
    const detectCardType = (cardNumber: string) => {
        const cleaned = cardNumber.replace(/\D/g, '');
        
        // American Express: 34, 37 (15 dígitos)
        if (/^3[47]/.test(cleaned)) {
            return { type: 'amex', length: 15, format: 'amex' };
        }
        
        // Visa: 4 (13 o 16 dígitos, pero generalmente 16)
        if (/^4/.test(cleaned)) {
            return { type: 'visa', length: 16, format: 'standard' };
        }
        
        // Mastercard: 51-55, 2221-2720 (16 dígitos)
        if (/^5[1-5]/.test(cleaned) || /^2(22[1-9]|2[3-9]\d|[3-6]\d{2}|7[01]\d|720)/.test(cleaned)) {
            return { type: 'mastercard', length: 16, format: 'standard' };
        }
        
        // Discover: 6011, 622126-622925, 644-649, 65 (16 dígitos)
        if (/^6(?:011|5|4[4-9]|22(1(2[6-9]|[3-9]\d)|[2-8]\d{2}|9([01]\d|2[0-5])))/.test(cleaned)) {
            return { type: 'discover', length: 16, format: 'standard' };
        }
        
        // Por defecto, asumimos formato estándar de 16 dígitos
        return { type: 'unknown', length: 16, format: 'standard' };
    };

    const formatCardNumber = (value: string) => {
        const cleaned = value.replace(/\D/g, '');
        const cardInfo = detectCardType(cleaned);
        
        if (cardInfo.format === 'amex') {
            // Formato Amex: 4-6-5 (ej: 3782 822463 10005)
            return cleaned
                .replace(/(\d{4})(\d{0,6})(\d{0,5})/, (match, p1, p2, p3) => {
                    let formatted = p1;
                    if (p2) formatted += ' ' + p2;
                    if (p3) formatted += ' ' + p3;
                    return formatted;
                })
                .substring(0, 17); // 15 dígitos + 2 espacios
        } else {
            // Formato estándar: 4-4-4-4
            return cleaned
                .replace(/(.{4})/g, '$1 ')
                .trim()
                .substring(0, 19); // 16 dígitos + 3 espacios
        }
    };

    const formatExpirationDate = (value: string) => {
        let cleanedValue = value.replace(/\D/g, '');

        if (cleanedValue.length > 2) {
            cleanedValue = cleanedValue.substring(0, 2) + '/' + cleanedValue.substring(2);
        }

        return cleanedValue.substring(0, 5);
    };

    const handleExpirationChange = (e: any) => {
        const formattedValue = formatExpirationDate(e.target.value);
        setFormValues({ ...formValues, card_expiration: formattedValue });

        validateExpirationDate(formattedValue);
    };

    const validateExpirationDate = (value: string) => {
        const [month, year] = value.split('/').map(Number);
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1; // Los meses son 0-indexados
        const currentYear = currentDate.getFullYear() % 100; // Obtener los últimos dos dígitos del año

        let isValid = true;

        // Validar que el mes no sea superior a 12
        if (month > 12 || month < 1) {
            isValid = false;
        }

        // Validar que la fecha sea superior a la presente
        if (year < currentYear || (year === currentYear && month < currentMonth)) {
            isValid = false;
        }

        setErrors({ ...errors, card_expiration: !isValid });
    };


    const submitPayment = async () => {
        
        if (!formValues.name || formValues.name.length < 2) {
            enqueueSnackbar('Please enter a valid name', { variant: 'error' });
            return;
        }

        // Validar longitud de tarjeta según el tipo detectado
        const cardDigits = formValues.card_number.replace(/\D/g, '');
        const cardInfo = detectCardType(cardDigits);
        
        if (!formValues.card_number || cardDigits.length !== cardInfo.length) {
            enqueueSnackbar('Please enter a valid card number', { variant: 'error' });
            return;
        }

        if (errors.card_expiration) {
            enqueueSnackbar('Please enter a valid expiration date', { variant: 'error' });
            return;
        }

        if (!formValues.card_cvv || formValues.card_cvv.length < 3) {
            enqueueSnackbar('Please enter a valid CVV', { variant: 'error' });
            return;
        }

        setLoading(true);
        try {
            await updatePlan(formValues, amount);
        } catch (error) {
            console.error("Payment error:", error);
        } finally {
            setLoading(false);
        }        
    }



    return (
        <div className="elavon_form_wrapper">
            <div className="input_group">
                <TextField 
                    label="Name"
                    variant="outlined"
                    value={formValues.name}
                    onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                />

                <TextField 
                    label="Card Number"
                    variant="outlined"
                    value={formValues.card_number}
                    onChange={(e) => setFormValues({ ...formValues, card_number: formatCardNumber(e.target.value) })}
                    inputProps={{ maxLength: 19 }}
                    helperText="Accepts Visa, Mastercard, and American Express"
                />

                <TextField 
                    label="Expiration Date (MM/AA)"
                    variant="outlined"
                    value={formValues.card_expiration}
                    onChange={handleExpirationChange}
                    error={errors.card_expiration}
                    helperText={errors.card_expiration ? "Invalid date MM/AA" : ""}
                />

                <TextField 
                    label="CVV"
                    variant="outlined"
                    value={formValues.card_cvv}
                    onChange={(e) => setFormValues({ ...formValues, card_cvv: e.target.value })}
                    inputProps={{ maxLength: 4 }}
                />

                    <div className="plan_buttons">
                        {style === 'custom' && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setOpenDialog(false)}
                            fullWidth
                        >
                            Cancel
                        </Button>
                        )}
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            onClick={submitPayment}
                            loading={loading}
                            fullWidth
                        >
                            Pay
                        </LoadingButton>

                    </div>

           
                    

                
            </div>
        </div>
    );
}
